import {gmAPI, MapService} from '@/services/MapService';
import {Graphic} from '../Graphic';
import {pSBC} from '../colorUtilities';
import {SubscriptionManager} from '@/lib/filters/subscriptions';
import {SVGOverlayView} from '../SVGOverlayView';
import * as colors from '@/lib/colors';

/**
 * @typedef {google.maps.PolygonOptions} PolygonData
 * @typedef {import('./GraphicService').GraphicData & PolygonData} PolygonGraphicData
 * @typedef {google.maps.LatLngLiteral} LatLngLiteral
 */


/** The vertex limit for polygons. Anything greater is assumed to be a large polygon. */
export const MAX_POLYGON_VERTICES = 999;

/** @type {google.maps.PolygonOptions} */
const polygonBaseOptions = {
  clickable: true,
  strokeOpacity: 1,
  strokeWeight: 4,
  fillOpacity: 0.15,
  strokeColor: colors.orange,
  fillColor: colors.orange,
  strokePosition: 1, // Setting enum value directly as there is no supported way to test this value https://developers.google.com/maps/documentation/javascript/reference/polygon#StrokePosition
};

/**
 * @param {google.maps.LatLng[]} path
 * @return {google.maps.LatLngBounds}
 */
function getBoundsFromPath(path) {
  const bounds = new gmAPI.LatLngBounds();
  path.forEach((point) => {
    bounds.extend(point);
  });
  return bounds;
}

/**
 * Returns the points of a polygon in an array.
 * @param {google.maps.Polygon} polygon
 * @return {google.maps.LatLngLiteral[]}
 */
function getPolygonPoints(polygon) {
  return polygon.getPath().getArray().map(({lat, lng}) => ({
    lat: lat(),
    lng: lng(),
  }));
}


/**
 * Calculates the total acreage of a polygon
 * @param {Array<Array<LatLngLiteral>>} paths
 * @return {number}
 */
function calculateTotalAcreage(paths) {
  const pathAsGMPath = [];

  let totalArea = 0;
  if (!paths || !Array.isArray(paths) || paths.length === 0) {
    console.error('No paths provided for area calculation.', paths);
    throw new Error('No paths provided for area calculation.');
  }
  // calculate the total area of all the polygons in a feature
  try {
    paths.forEach((path) => {
      if (!Array.isArray(path) || path.length === 0) {
        console.error('Invalid path provided for area calculation:', path);
        throw new Error('Invalid path provided for area calculation.');
      }
      path.forEach((coords) => {
        const latlng = new gmAPI.LatLng(coords.lat, coords.lng);
        pathAsGMPath.push(latlng);
      });
      // 0.000247105 is the conversion factor from square meters to acres.
      totalArea += gmAPI.geometry.spherical.computeArea(pathAsGMPath) * 0.000247105;
    });
  } catch (error) {
    error.message = `Error calculating area for polygon: ${error.message}`;
    throw error;
  }

  return parseFloat(totalArea.toFixed(2));
}

/**
 * Calculates the total acreage of a polygon
 * @param {Array<Array<LatLngLiteral>>} paths
 * @return {number}
 */
function calculateSF(paths) {
  const pathAsGMPath = [];

  let totalArea = 0;
  paths.forEach((path) => {
    path.forEach((coords) => {
      const latlng = new gmAPI.LatLng(coords.lat, coords.lng);
      pathAsGMPath.push(latlng);
    });
    // 10.7639 is the conversion factor from square meters to square feet.
    totalArea += gmAPI.geometry.spherical.computeArea(pathAsGMPath) * 10.7639;
  });

  return parseFloat(totalArea.toFixed(2));
}

/**
 * @classdesc Class representing a polygon graphic.
 * @class
 * @augments Graphic
 */
class PolygonGraphic extends Graphic {
  /**
   * Creates a new PolygonGraphic object. Can be initialized from an existing polygon, or from a PolygonOptions object.
   * @param {PolygonGraphicData|google.maps.Polygon} polygonData The options for the polygon.
   */
  constructor(polygonData) {
    super();

    // Store polygonData for switching between SVG and Polygon representations
    this.polygonData = polygonData;

    /** @type {google.maps.Polygon[]} */
    this.polygons = [];

    /** @type {Array<Array<LatLngLiteral>>} 2D array of lat/lng objects */
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- JSDoc type declaration
    this.paths;

    // Case 1: the oldest polygons are a single-poly array of lat/lng objects in `path`
    // Case 2: newer multi-polys in `polys` are an object with keys as indices and values as arrays of lat/lng objects
    // Case 3: newest multi-polys are an array of encoded strings which need to be decoded
    if (this.polygonData.path) {
      this.paths = [this.polygonData.path];
    } else if (Array.isArray(this.polygonData.polys)) {
      if (typeof this.polygonData.polys[0] === 'object') {
        this.paths = this.polygonData.polys;
      } else {
        this.paths = this.polygonData.polys.map((poly) => gmAPI.geometry.encoding.decodePath(poly));
      }
    } else if (this.polygonData.polys) {
      this.paths = Array.from(Object.values(this.polygonData.polys));
    }

    /** @type {boolean} */
    this.oversizePoly = this.paths.reduce((acc, path) => acc + path.length, 0) > MAX_POLYGON_VERTICES;

    try {
      /** @type {number} */
      this.totalAcreage = calculateTotalAcreage(this.paths);
    } catch (error) {
      error.message = `Error calculating acreage for polygon ${polygonData.title}: ${error.message}`;
      console.error(error);
      this.totalAcreage = 0;
    }

    /** @type {string} */
    // Default to standard orange if no color is available
    this.color = polygonData.color ?? polygonData.strokeColor ?? polygonData.fillColor ?? '#e18d3d';

    /** @type {number} */
    this.fillOpacity = polygonData.fillOpacity ?? polygonBaseOptions.fillOpacity;

    // Force SVG paths to be complete if they somehow aren't
    this.paths.forEach((path) => {
      if (path[0] !== path[path.length - 1]) {
        path.push(path[0]);
      }
    });

    // Create SVG and display it first
    this.svgOverlay = new SVGOverlayView({
      ...polygonBaseOptions,
      ...polygonData,
      paths: this.paths,
      strokeColor: this.color,
      fillColor: this.color,
      fillOpacity: this.fillOpacity,
    }, this.uuid);

    this.svgOverlay.setMap(MapService.getMap());

    this.editing = false;

    /** @type {boolean} */
    this._clickable = false;
    /** @type {SubscriptionManager} */
    this.subscriptions = new SubscriptionManager();
  }

  /** @param {boolean} enabled */
  setPointerEvents(enabled) {
    this.svgOverlay.setPointerEvents(enabled);
  }

  /**
   * Adds the Polygon representation to the map using a 2D array of lat/lng objects.
   */
  displayPolygon() {
    // Remove SVG representation
    if (this.svgOverlay) {
      this.svgOverlay.setMap(null);
    }

    // Build Polygon representation
    if (this.polygonData instanceof gmAPI.Polygon) {
      this.polygons.push(this.polygonData);
    } else {
      const defaultOptions = Object.assign({}, polygonBaseOptions, this.polygonData);
      delete defaultOptions.polys;

      // Use 2D lat/lng literals since Firestore does not like custom types being saved
      this.paths.forEach((path) => {
        // Remove the duplicate final vertex so that the user doesn't have double vertices
        const pathWithoutDuplicate = path.slice(0, -1);
        this.polygons.push(new gmAPI.Polygon({...defaultOptions, path: pathWithoutDuplicate}));
      });
    }

    // Set listeners for changes using default Maps polygon handles.
    this.polygons.forEach((p) => {
      // Make the polygon editable (adds the handles to the edges for resizing)
      p.setEditable(true);
      // Make the polygon highlighted
      p.setOptions({
        strokeColor: this.color ?? pSBC(0.5, this.color, '#fff'),
        fillColor: this.color,
        fillOpacity: this.fillOpacity,
        zIndex: 1000,
        id: this.uuid,
      });
      ['insert_at', 'set_at', 'remove_at'].forEach((eventType) => {
        p.getPath().addListener(eventType, () => {
          this.paths = [];
          this.polygons.forEach((p) => {
            this.paths.push(getPolygonPoints(p));
          });
          // Ensure that paths are a closed polygon
          this.paths.forEach((path) => {
            const lastPathIndex = path.length - 1;
            if (!this.arePathsEqual(path[lastPathIndex], path[0])) {
              path.push(path[0]);
            }
          });
          this.totalAcreage = calculateTotalAcreage(this.paths);
          // emit a graphic changed event and tell the feature to update total acreage df
          this.subscriptions.publish('graphic_changed', {
            recalcDisplayFields: [
              {
                label: 'Acreage Calculated',
                value: this.totalAcreage,
              },
              {
                label: 'Calculated SF',
                value: calculateSF(this.paths),
              },
            ],
          });
        });
      });
      p.addListener('click', (event) => this.handlePolygonClick(event, p));
    });

    this.polygons.forEach((poly) => poly.setMap(MapService.getMap()));

    // Open the info window with a toolbar
    this.subscriptions.publish('open_info_window', {
      toolbar: this.getToolbarOptions(),
      featureType: 'polygon',
    });
  }

  /**
   * Adds the SVG representation to the map using a 2D array of lat/lng objects.
   */
  displaySvg() {
    // Display SVG
    if (this.svgOverlay) {
      // Apply any new coloring to the svg
      this.svgOverlay.setHighlighted(false);

      // Must use 2D array of lat/lng literals because Firestore doesn't like custom types stored
      const paths = this.paths;

      this.svgOverlay.setMap(MapService.getMap());
      this.svgOverlay.setOptions({
        paths: paths, strokeColor: this.color, fillColor: this.color, fillOpacity: this.fillOpacity,
      });

      // Stop displaying Polygon representation
      this.polygons.forEach((poly) => poly.setMap(null));
      this.polygons = [];
    }
  }

  arePathsEqual(path1, path2) {
    const keys1 = Object.keys(path1);
    const keys2 = Object.keys(path2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (path1[key] !== path2[key]) {
        return false;
      }
    }

    return true;
  }

  addLocalHandler(eventType, handlerName, handler) {
    // The SVGOverlayView does not care about the handlerName.
    this.svgOverlay.addListener(eventType, handler);
  }

  removeLocalHandler(eventType, handlerName, handler) {
    // The SVGOverlayView does not care about the handlerName.
    this.svgOverlay.removeListener(eventType, handler);
  }

  setClickable(/** @type {boolean} */ clickable) {
    this._clickable = clickable;
    this.polygons.forEach((poly) => poly.setOptions({clickable: clickable}));
  }

  isClickable() {
    return this._clickable;
  }

  setMap(/** @type {google.maps.Map|null} */ map) {
    this.svgOverlay.setMap(map);
    this.polygons.forEach((poly) => poly.setMap(null));
  }

  setHighlight(/** @type {boolean} */ highlighted) {
    // setHighlight should affect the SVG first since the SVG is displayed by default
    this.svgOverlay.setHighlighted(highlighted);
  }

  setEditing(editing) {
    if (this.editing === editing) return;

    this.editing = editing;

    if (editing) {
      // The browser will crash if users try to edit the vertices of oversize polygons
      // so don't convert it to a polygon - just display the SVG and open the infowindow
      if (this.oversizePoly) {
        this.svgOverlay.setHighlighted(true);
        this.subscriptions.publish('open_info_window', {
          toolbar: this.getToolbarOptions(),
          featureType: 'polygon',
        });
        return;
      }

      this.displayPolygon();
    } else {
      this.displaySvg();
    }
  }

  handlePolygonClick(event, polygon) {
    if (event.domEvent.altKey) {
      // Remove vertex if alt key is held
      if ((event.vertex || event?.vertex === 0) && polygon.getPath().getLength() > 2) {
        polygon.getPath().removeAt(event.vertex);
      }
    }
  }

  subscribe(eventName, callback) {
    return this.subscriptions.addSubscription(eventName, callback);
  }

  toJSON() {
    const superJSON = super.toJSON();
    const polys = this.paths.map((path) => gmAPI.geometry.encoding.encodePath(path));
    const bounds = getBoundsFromPath(this.paths.flat()).toJSON();
    return {
      ...superJSON,
      bounds,
      graphicData: {
        bounds,
        clickable: true,
        strokeColor: this.color,
        fillColor: this.color,
        fillOpacity: this.fillOpacity,
        featureType: 'polygon',
        polys,
      },
    };
  }

  getToolbarOptions() {
    return {
      toolbarOptions: [
        [
          {
            icon: 'Fill',
            action: 'colorPicker',
            name: 'Change polygon color',
            pickers: [
              {
                title: 'Parcel Color',
                getColor: () => this.color,
                setColor: (color) => {
                  this.color = color;
                  this.polygons.forEach((p) => p.setOptions({strokeColor: this.color, fillColor: this.color}));
                  this.svgOverlay.setOptions({strokeColor: this.color, fillColor: this.color});
                  this.subscriptions.publish('graphic_changed');
                },
                allowTransparent: false,
                availableColors: colors.overlayColorPalette,
              },
            ],
            slider: {
              label: 'Opacity',
              min: 0,
              max: 100,
              step: 1,
              getColor: () => this.color,
              getValue: () => Math.round(this.fillOpacity * 100),
              setValue: (value) => {
                this.fillOpacity = value / 100;
                this.polygons.forEach((p) => p.setOptions({fillOpacity: this.fillOpacity}));
                this.svgOverlay.setOptions({fillOpacity: this.fillOpacity});
                this.subscriptions.publish('graphic_changed');
              },
            },
          },
        ],
      ],
    };
  }

  /**
   * @param {number} index
   */
  setZIndex(index) {
    this.svgOverlay.setZIndex(index);
  }

  getLocation() {
    const paths = this.paths.at(0);
    const middleIndex = Math.floor(paths.length / 2);
    return {lat: paths[middleIndex].lat, lng: paths[middleIndex].lng};
  }
}

/**
 * Creates a new PolygonGraphic object. Can be initialized from an existing polygon, or from a PolygonOptions object.
 * @param {PolygonGraphicData|google.maps.Polygon} polygonData The options for the polygon.
 * @return {PolygonGraphic} The new PolygonGraphic object.
 */
function createPolygonGraphic(polygonData) {
  return new PolygonGraphic(polygonData);
}

export {
  createPolygonGraphic,
  PolygonGraphic,
  calculateTotalAcreage,
  calculateSF,
};
